import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useApp } from "@/context/AppContext";
import { Search, ShoppingBag, Heart, User, Menu, X, Sparkles } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import KitFinderModal from "@/components/KitFinderModal";
import { DivineCartLogo } from "@/components/Logo";

const NAV = [
  { label: "Puja Samagri", to: "/category/puja-samagri" },
  { label: "Puja Kits", to: "/kits" },
  { label: "Havan", to: "/category/havan-samagri" },
  { label: "Idols", to: "/category/idols-murtis" },
  { label: "Diyas", to: "/category/diyas" },
  { label: "Agarbatti", to: "/category/agarbatti" },
  { label: "Rudraksha", to: "/category/rudraksha" },
  { label: "Festivals", to: "/festivals" },
  { label: "Best Sellers", to: "/best-sellers" },
  { label: "Puja Guides", to: "/guides" },
];

export default function Header() {
  const { user, cartCount, logout } = useApp();
  const [q, setQ] = useState("");
  const [finderOpen, setFinderOpen] = useState(false);
  const nav = useNavigate();
  const submit = (e) => { e.preventDefault(); if (q.trim()) nav(`/search?q=${encodeURIComponent(q.trim())}`); };

  return (
    <>
    <header className="sticky top-0 z-40 glass-header" data-testid="site-header">
      {/* Top strip */}
      <div className="bg-primary text-primary-foreground text-xs">
        <div className="max-w-7xl mx-auto px-4 py-2 flex items-center justify-between">
          <span className="devanagari tracking-wide">🕉 शुद्ध सामग्री • भरोसेमंद ब्रांड • पूरे भारत में डिलीवरी</span>
          <span className="hidden sm:inline">Free Shipping on orders above ₹399 • COD Available</span>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-4 py-4 flex items-center gap-6">
        <Link to="/" data-testid="logo-link" className="flex items-center group">
          <DivineCartLogo />
        </Link>

        <form onSubmit={submit} className="hidden md:flex flex-1 max-w-xl">
          <div className="relative w-full">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input data-testid="search-input" value={q} onChange={(e)=>setQ(e.target.value)}
              placeholder="Search for diya, kapoor, rudraksha, puja kit..." 
              className="w-full pl-11 pr-4 py-2.5 rounded-full bg-white border border-border text-sm outline-none focus:border-primary" />
          </div>
        </form>

        <div className="flex items-center gap-2 md:gap-3 ml-auto">
          <button data-testid="open-kit-finder-btn" onClick={()=>setFinderOpen(true)}
            className="hidden md:inline-flex items-center gap-2 rounded-full text-white px-5 py-2.5 text-sm font-medium"
            style={{background:"linear-gradient(135deg,#F47920,#D4AF37)", boxShadow:"0 8px 20px -8px rgba(244,121,32,.5)"}}>
            <Sparkles className="w-4 h-4" /> Find My Puja Kit
          </button>
          {user ? (
            <div className="hidden md:flex items-center gap-2">
              <Link to="/account" data-testid="account-link" className="p-2 rounded-full hover:bg-muted"><User className="w-5 h-5"/></Link>
              {user.role === "admin" && <Link to="/admin" data-testid="admin-link" className="text-xs font-semibold text-primary underline">Admin</Link>}
              <button data-testid="logout-btn" onClick={logout} className="text-xs text-muted-foreground hover:text-primary">Logout</button>
            </div>
          ) : (
            <Link to="/login" data-testid="login-link" className="hidden md:inline text-sm hover:text-primary">Login</Link>
          )}
          <Link to="/wishlist" data-testid="wishlist-link" className="p-2 rounded-full hover:bg-muted"><Heart className="w-5 h-5"/></Link>
          <Link to="/cart" data-testid="cart-link" className="relative p-2 rounded-full hover:bg-muted">
            <ShoppingBag className="w-5 h-5"/>
            {cartCount > 0 && <span className="absolute -top-1 -right-1 bg-accent text-white text-[10px] rounded-full w-5 h-5 grid place-items-center font-bold">{cartCount}</span>}
          </Link>

          <Sheet>
            <SheetTrigger className="md:hidden p-2" data-testid="mobile-menu-btn"><Menu className="w-5 h-5"/></SheetTrigger>
            <SheetContent side="right" className="bg-background">
              <div className="mt-6 space-y-1">
                {NAV.map(n => <Link key={n.to} to={n.to} className="block py-2 border-b border-border">{n.label}</Link>)}
                <button onClick={()=>setFinderOpen(true)} className="mt-4 pk-btn-accent w-full"><Sparkles className="w-4 h-4"/>Find My Puja Kit</button>
                {user ? <>
                  <Link to="/account" className="block py-2 mt-4">My Account</Link>
                  {user.role==="admin" && <Link to="/admin" className="block py-2">Admin</Link>}
                  <button onClick={logout} className="block py-2 text-primary">Logout</button>
                </> : <Link to="/login" className="block py-2 mt-4 text-primary">Login / Register</Link>}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
      <nav className="hidden md:block border-t border-border/60">
        <div className="max-w-7xl mx-auto px-4">
          <ul className="flex items-center gap-6 overflow-x-auto no-scrollbar py-3 text-sm">
            {NAV.map(n => (
              <li key={n.to}>
                <Link data-testid={`nav-${n.to.replace(/\//g,"-")}`} to={n.to} className="whitespace-nowrap hover:text-primary transition-colors">
                  {n.label}
                </Link>
              </li>
            ))}
            <li className="ml-auto"><Link to="/offers" className="text-accent font-semibold whitespace-nowrap">🎁 Offers</Link></li>
          </ul>
        </div>
      </nav>
    </header>
    <KitFinderModal open={finderOpen} onClose={()=>setFinderOpen(false)} />
    </>
  );
}
